import { Component, type ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { StandardAction, StandardState } from '@/components/standard';

type PageErrorBoundaryProps = {
  children: ReactNode;
  resetKey: string;
};

type PageErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
  capturedKey: string;
};

export class PageErrorBoundary extends Component<PageErrorBoundaryProps, PageErrorBoundaryState> {
  constructor(props: PageErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, capturedKey: props.resetKey };
  }

  static getDerivedStateFromError(error: Error): Partial<PageErrorBoundaryState> {
    return { hasError: true, error };
  }

  static getDerivedStateFromProps(
    props: PageErrorBoundaryProps,
    state: PageErrorBoundaryState,
  ): Partial<PageErrorBoundaryState> | null {
    if (props.resetKey === state.capturedKey) return null;
    return { hasError: false, error: null, capturedKey: props.resetKey };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[PageErrorBoundary] Caught render error:', {
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-[60dvh] items-center justify-center bg-[#0e0e0e] px-4 py-12 sm:px-6">
          <StandardState
            kind="error"
            surface="dark"
            title="This page needs a reload"
            description="Your saved work is still protected. Reload the page or go back and keep moving."
            actionLabel="Reload"
            onAction={() => window.location.reload()}
            secondaryAction={(
              <StandardAction
                variant="secondary"
                onClick={() => { if (window.history.length > 1) window.history.back(); else window.location.href = '/client'; }}
                className="border-white/15 bg-white/[0.04] text-white hover:bg-white/[0.08]"
              >
                <ArrowLeft className="size-4" aria-hidden="true" /> Back
              </StandardAction>
            )}
            className="w-full max-w-md"
          />
        </div>
      );
    }
    return this.props.children;
  }
}
